/**
 * Dental domain constants: FDI tooth numbers, charting conditions and the
 * procedure catalogue used to build treatment plans.
 *
 * Condition keys are a closed set shared with the backend
 * (kaagazy-backend/app/modules/visit/dental.py, DENTAL_CONDITIONS), which
 * rejects any other key. A backend test reads this file and fails when the two
 * lists differ, so add a condition in both places in the same PR.
 *
 * Procedure codes are free to grow here: the backend only checks their shape
 * (lowercase letters, digits and underscores, at most 50 characters).
 */

// ─── FDI tooth numbers ────────────────────────────────────────────────────────

/** Adult teeth: quadrants 1-4, positions 1-8. */
export const PERMANENT_TEETH: readonly number[] = [1, 2, 3, 4].flatMap((q) =>
  [1, 2, 3, 4, 5, 6, 7, 8].map((p) => q * 10 + p),
);

/** Milk teeth: quadrants 5-8, positions 1-5. */
export const PRIMARY_TEETH: readonly number[] = [5, 6, 7, 8].flatMap((q) =>
  [1, 2, 3, 4, 5].map((p) => q * 10 + p),
);

// ─── Conditions ───────────────────────────────────────────────────────────────

/**
 * "disease" conditions drive treatment suggestions. "state" conditions record
 * work already done (or a gap) and mostly suggest nothing, except a missing
 * tooth (replacement) and a root-canal-treated tooth without a crown.
 */
export type ConditionGroup = "disease" | "state";

export const DENTAL_CONDITIONS = [
  { key: "caries",                label: "Caries",                group: "disease", color: "#f97316" },
  { key: "deep_caries",           label: "Deep caries",           group: "disease", color: "#c2410c" },
  { key: "reversible_pulpitis",   label: "Reversible pulpitis",   group: "disease", color: "#eab308" },
  { key: "irreversible_pulpitis", label: "Irreversible pulpitis", group: "disease", color: "#ef4444" },
  { key: "periapical_abscess",    label: "Periapical abscess",    group: "disease", color: "#991b1b" },
  { key: "fracture",              label: "Fracture",              group: "disease", color: "#db2777" },
  { key: "impacted",              label: "Impacted",              group: "disease", color: "#7c3aed" },
  { key: "root_stump",            label: "Root stump",            group: "disease", color: "#78350f" },
  { key: "attrition",             label: "Attrition / abrasion",  group: "disease", color: "#0d9488" },
  { key: "stain",                 label: "Stain",                 group: "disease", color: "#64748b" },
  { key: "pyorrhea",              label: "Pyorrhea",              group: "disease", color: "#a855f7" },
  { key: "recession",             label: "Recession",             group: "disease", color: "#0ea5e9" },
  { key: "root_canal_treated",    label: "Root canal treated",    group: "state",   color: "#3b82f6" },
  { key: "missing",               label: "Missing",               group: "state",   color: "#94a3b8" },
  { key: "crown",                 label: "Crown",                 group: "state",   color: "#10b981" },
  { key: "implant",               label: "Implant",               group: "state",   color: "#1e293b" },
] as const satisfies readonly { key: string; label: string; group: ConditionGroup; color: string }[];

export type ConditionKey = (typeof DENTAL_CONDITIONS)[number]["key"];

export const CONDITION_BY_KEY: Readonly<Record<ConditionKey, (typeof DENTAL_CONDITIONS)[number]>> =
  Object.fromEntries(DENTAL_CONDITIONS.map((c) => [c.key, c])) as never;

// ─── Procedure catalogue ──────────────────────────────────────────────────────

/** Where a procedure applies: one tooth, one quadrant, or the whole mouth. */
export type ProcedureScope = "tooth" | "quadrant" | "mouth";

export type ProcedureGroup =
  | "diagnostic" | "preventive" | "restorative" | "endodontic"
  | "prosthodontic" | "periodontal" | "surgical" | "cosmetic" | "paediatric";

export interface Procedure {
  code: string;
  /** In CAPITALS, as it prints on the prescription. */
  label: string;
  group: ProcedureGroup;
  scope: ProcedureScope;
  /** Typical number of sittings; the dentist can change it per plan item. */
  sittings: number;
  /** Set when the procedure only applies to milk teeth or only to adult teeth. */
  dentition?: "primary" | "permanent";
}

export const PROCEDURES = [
  // Diagnostic
  { code: "iopa",                  label: "IOPA X-RAY",                 group: "diagnostic",    scope: "tooth",    sittings: 1 },
  { code: "opg",                   label: "OPG",                        group: "diagnostic",    scope: "mouth",    sittings: 1 },
  { code: "cbct_referral",         label: "CBCT REFERRAL",              group: "diagnostic",    scope: "mouth",    sittings: 1 },
  // Preventive
  { code: "oral_prophylaxis",      label: "ORAL PROPHYLAXIS",           group: "preventive",    scope: "mouth",    sittings: 1 },
  { code: "fluoride_application",  label: "FLUORIDE APPLICATION",       group: "preventive",    scope: "mouth",    sittings: 1 },
  { code: "pit_fissure_sealant",   label: "PIT AND FISSURE SEALANT",    group: "preventive",    scope: "tooth",    sittings: 1 },
  // Restorative. Plan rows follow this order, so pulp capping sits above the filling.
  { code: "indirect_pulp_capping", label: "INDIRECT PULP CAPPING",      group: "restorative",   scope: "tooth",    sittings: 1 },
  { code: "direct_pulp_capping",   label: "DIRECT PULP CAPPING",        group: "restorative",   scope: "tooth",    sittings: 1 },
  { code: "composite_restoration", label: "COMPOSITE RESTORATION",      group: "restorative",   scope: "tooth",    sittings: 1 },
  { code: "gic_restoration",       label: "GIC RESTORATION",            group: "restorative",   scope: "tooth",    sittings: 1 },
  { code: "amalgam_restoration",   label: "AMALGAM RESTORATION",        group: "restorative",   scope: "tooth",    sittings: 1 },
  // Endodontic
  { code: "rct_anterior",          label: "RCT - ANTERIOR",             group: "endodontic",    scope: "tooth",    dentition: "permanent", sittings: 2 },
  { code: "rct_premolar",          label: "RCT - PREMOLAR",             group: "endodontic",    scope: "tooth",    dentition: "permanent", sittings: 2 },
  { code: "rct_molar",             label: "RCT - MOLAR",                group: "endodontic",    scope: "tooth",    dentition: "permanent", sittings: 3 },
  { code: "re_rct",                label: "RE-RCT",                     group: "endodontic",    scope: "tooth",    dentition: "permanent", sittings: 3 },
  { code: "post_and_core",         label: "POST AND CORE",              group: "endodontic",    scope: "tooth",    dentition: "permanent", sittings: 1 },
  { code: "pulpotomy",             label: "PULPOTOMY",                  group: "endodontic",    scope: "tooth",    dentition: "primary", sittings: 1 },
  { code: "pulpectomy",            label: "PULPECTOMY",                 group: "endodontic",    scope: "tooth",    dentition: "primary", sittings: 2 },
  // Prosthodontic
  { code: "crown_metal",           label: "CROWN - METAL",              group: "prosthodontic", scope: "tooth",    dentition: "permanent", sittings: 2 },
  { code: "crown_pfm",             label: "CROWN - PFM",                group: "prosthodontic", scope: "tooth",    dentition: "permanent", sittings: 2 },
  { code: "crown_zirconia",        label: "CROWN - ZIRCONIA",           group: "prosthodontic", scope: "tooth",    dentition: "permanent", sittings: 2 },
  { code: "crown_emax",            label: "CROWN - E-MAX",              group: "prosthodontic", scope: "tooth",    dentition: "permanent", sittings: 2 },
  { code: "bridge",                label: "BRIDGE",                     group: "prosthodontic", scope: "tooth",    dentition: "permanent", sittings: 2 },
  { code: "rpd",                   label: "REMOVABLE PARTIAL DENTURE",  group: "prosthodontic", scope: "tooth",    dentition: "permanent", sittings: 3 },
  { code: "complete_denture",      label: "COMPLETE DENTURE",           group: "prosthodontic", scope: "mouth",    sittings: 4 },
  { code: "implant",               label: "IMPLANT WITH CROWN",         group: "prosthodontic", scope: "tooth",    dentition: "permanent", sittings: 3 },
  { code: "stainless_steel_crown", label: "STAINLESS STEEL CROWN",      group: "prosthodontic", scope: "tooth",    dentition: "primary", sittings: 1 },
  // Periodontal
  { code: "srp",                   label: "SCALING AND ROOT PLANING",   group: "periodontal",   scope: "quadrant", sittings: 1 },
  { code: "curettage",             label: "CURETTAGE",                  group: "periodontal",   scope: "quadrant", sittings: 1 },
  { code: "flap_surgery",          label: "FLAP SURGERY",               group: "periodontal",   scope: "quadrant", sittings: 1 },
  { code: "gingival_graft",        label: "GINGIVAL GRAFT",             group: "periodontal",   scope: "tooth",    sittings: 1 },
  { code: "splinting",             label: "SPLINTING",                  group: "periodontal",   scope: "tooth",    sittings: 1 },
  { code: "desensitising",         label: "DESENSITISING TREATMENT",    group: "periodontal",   scope: "tooth",    sittings: 1 },
  // Surgical
  { code: "extraction",            label: "EXTRACTION",                 group: "surgical",      scope: "tooth",    sittings: 1 },
  { code: "surgical_extraction",   label: "SURGICAL EXTRACTION",        group: "surgical",      scope: "tooth",    sittings: 1 },
  { code: "impaction_removal",     label: "IMPACTED TOOTH REMOVAL",     group: "surgical",      scope: "tooth",    dentition: "permanent", sittings: 1 },
  // Cosmetic
  { code: "bleaching",             label: "BLEACHING",                  group: "cosmetic",      scope: "mouth",    sittings: 2 },
  { code: "veneer",                label: "VENEER",                     group: "cosmetic",      scope: "tooth",    dentition: "permanent", sittings: 2 },
  // Paediatric
  { code: "space_maintainer",      label: "SPACE MAINTAINER",           group: "paediatric",    scope: "tooth",    dentition: "primary", sittings: 2 },
] as const satisfies readonly Procedure[];

export type ProcedureCode = (typeof PROCEDURES)[number]["code"];

export const PROCEDURE_BY_CODE: Readonly<Record<ProcedureCode, Procedure>> =
  Object.fromEntries(PROCEDURES.map((p) => [p.code, p])) as never;
